import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { get } from "lib/api";
import { firstListMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import MxShell from "../components/MxShell";
import { MxGridTile } from "../components/MxProductCard";
import { MxFilterPill } from "../components/MxUi";
import { formatMoney, mx, normalizeTransmissions, resolvePrice } from "../lib/theme";

function normalizeProduct(item) {
  const attrs = item?.attributes || item || {};
  return {
    documentId: item?.documentId || attrs?.documentId,
    sku: attrs.sku,
    name: attrs.name,
    unitPrice: attrs.unitPrice,
    priceLevels: attrs.priceLevels,
    images: attrs.images,
    transmissions: normalizeTransmissions(attrs.transmissions),
    imageUrl: firstListMediaUrl(attrs.images),
  };
}

export default function CatalogPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = String(searchParams.get("q") || "");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedTransmission, setSelectedTransmission] = useState("");
  const { token, storefrontTenant } = useAuth();
  const { t } = useStorefrontCopy();

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(q), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ["mx-mobile-catalog", debouncedSearch, storefrontTenant || "mx", token ? "auth" : "public"],
    queryFn: () =>
      get("/api/storefront/catalog", {
        q: debouncedSearch || undefined,
        sort: "name:asc",
        pagination: { pageSize: 100 },
      }),
    staleTime: 30000,
  });

  const currency = data?.meta?.company?.baseCurrency || currencyForCompany(storefrontTenant || "mx");
  const products = useMemo(() => (data?.data || []).map(normalizeProduct), [data]);

  const transmissionOptions = useMemo(() => {
    const set = new Set();
    products.forEach((product) => (product.transmissions || []).forEach((value) => set.add(value)));
    return Array.from(set).sort().slice(0, 8);
  }, [products]);

  const visibleProducts = useMemo(() => {
    if (!selectedTransmission) return products;
    return products.filter((product) =>
      (product.transmissions || []).some((value) => value.toLowerCase() === selectedTransmission.toLowerCase()),
    );
  }, [products, selectedTransmission]);

  const setSearch = (value) => {
    const next = String(value || "").trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  return (
    <MxShell
      title={t("common.catalog", "Productos")}
      onBack={() => navigate("/m")}
    >
      <TextField
        fullWidth
        value={q}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t("common.searchPlaceholder", "Buscar cuerpos de válvulas...")}
        variant="standard"
        InputProps={{
          disableUnderline: false,
          startAdornment: (
            <InputAdornment position="start">
              <Icon sx={{ color: mx.subtle, fontSize: 22 }}>search</Icon>
            </InputAdornment>
          ),
        }}
        sx={{
          mb: 1.5,
          "& .MuiInput-underline:before": { borderBottomColor: mx.hairline },
          "& .MuiInput-underline:after": { borderBottomColor: mx.accent },
          "& .MuiInputBase-input": { fontSize: 16, py: 0.75 },
        }}
      />

      {transmissionOptions.length ? (
        <Stack
          direction="row"
          spacing={0.65}
          sx={{
            overflowX: "auto",
            pb: 1.75,
            mx: -2,
            px: 2,
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <MxFilterPill
            active={!selectedTransmission}
            label={t("common.allTransmissions", "Todas")}
            onClick={() => setSelectedTransmission("")}
          />
          {transmissionOptions.map((option) => (
            <MxFilterPill
              key={option}
              active={selectedTransmission === option}
              label={option}
              onClick={() => setSelectedTransmission(option)}
            />
          ))}
        </Stack>
      ) : null}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.25 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 650, color: mx.muted }}>
          {visibleProducts.length}
        </Typography>
      </Box>

      {isLoading ? (
        <Typography sx={{ color: mx.muted, py: 4, textAlign: "center" }}>{t("common.loadingCatalog", "Cargando catálogo…")}</Typography>
      ) : visibleProducts.length ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 2,
            rowGap: 2.5,
          }}
        >
          {visibleProducts.map((product) => (
            <MxGridTile
              key={product.documentId}
              product={product}
              priceLabel={formatMoney(resolvePrice(product), currency, t("catalog.priceQuote", "Cotización"))}
              onOpen={() => navigate(`/m/catalog/${product.documentId}`)}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 17, fontWeight: 700 }}>{t("catalog.emptyTitle", "No se encontraron productos")}</Typography>
          <Typography sx={{ color: mx.muted, mt: 0.5, fontSize: 14.5 }}>{t("catalog.emptyText", "Intente ajustar su búsqueda o filtros.")}</Typography>
        </Box>
      )}
    </MxShell>
  );
}
